import { useCallback, useContext, useEffect, useRef } from 'react';

import { useSelector } from 'react-redux';

import { CurrentDataContext } from 'src/app/current-data';
import { ensureCurlyBrackets } from 'src/components/navigation/src/utils/use-pref';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { preferences as prefs, PreferenceKey } from 'src/utils/preferences';
import {
  userRepoPreferences,
  userWorkspacePreferences,
} from 'src/utils/scoped-preferences';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { useObjectState } from './use-object-state';

type State = {
  value?: string;
  isLoading: boolean;
};

export type UpdatePref = (pref?: string | undefined) => Promise<void>;

export type UsePrefReturnType = [string | undefined, UpdatePref, boolean];

export function usePref(
  key: PreferenceKey,
  enabled: boolean = true
): UsePrefReturnType {
  const [state, setState] = useObjectState<State>({ isLoading: true });
  const currentUser = useSelector(getCurrentUser);
  const mounted = useRef(true);

  useEffect(() => {
    (async () => {
      if (!enabled) {
        return;
      }
      try {
        if (currentUser) {
          const value = await prefs.get(currentUser.uuid, key);
          if (mounted.current) {
            setState({ value, isLoading: false });
          }
        }
      } catch (e) {
        if (e) {
          captureExceptionWithTags(e, {
            component: 'usePref',
            preferenceKey: String(key),
          });
        }
      }
    })();
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser && currentUser.uuid, enabled]);

  const update = useCallback(
    async function (pref?: string) {
      try {
        if (!currentUser?.uuid) {
          return;
        }
        if (mounted.current) {
          setState({ isLoading: true });
        }
        if (pref === undefined) {
          await prefs.delete(currentUser.uuid, key);
        } else {
          await prefs.set(currentUser.uuid, key, pref);
        }
        if (mounted.current) {
          setState({ value: pref, isLoading: false });
        }
      } catch (e) {
        if (e) {
          captureExceptionWithTags(e, {
            component: 'usePref',
            preferenceKey: String(key),
          });
        }
      }
    },
    [currentUser?.uuid, key, setState]
  );

  return [state.value, update, state.isLoading];
}

function useScopedPref(
  key: PreferenceKey,
  // We accept a null uuid because we can't conditionally call hooks and
  // there's not a great way to get (e.g.) repo IDs that can never be null.
  // Forcing this value to be present would make calling code significantly
  // more complex.
  scopeUuid: string | null,
  scopeType: 'workspace' | 'repository',
  enabled: boolean = true
): UsePrefReturnType {
  // NOTE: This is mostly a copy/paste from userPref; it should be possible to refactor these.
  // There's also a similar function in components/navigation/src/utils/use-pref.ts
  const [state, setState] = useObjectState<State>({ isLoading: true });
  const currentUser = useSelector(getCurrentUser);
  const mounted = useRef(true);
  const prefsInterface =
    scopeType === 'workspace' ? userWorkspacePreferences : userRepoPreferences;

  useEffect(() => {
    (async () => {
      if (!enabled) {
        return;
      }
      try {
        if (currentUser?.uuid && scopeUuid) {
          const value = await prefsInterface.get(
            currentUser.uuid,
            scopeUuid,
            key
          );
          if (mounted.current) {
            setState({ value, isLoading: false });
          }
        }
      } catch (e) {
        if (e) {
          captureExceptionWithTags(e, {
            component: 'useScopedPref',
            preferenceKey: String(key),
            prefsScope: scopeType,
          });
        }
        if (mounted.current) {
          setState({ isLoading: false });
        }
      }
    })();
    return () => {
      mounted.current = false;
    };
  }, [
    currentUser?.uuid,
    key,
    prefsInterface,
    scopeUuid,
    setState,
    enabled,
    scopeType,
  ]);

  const update = useCallback(
    async function (pref?: string) {
      try {
        if (!currentUser?.uuid || !scopeUuid) {
          return;
        }
        if (mounted.current) {
          setState({ isLoading: true });
        }
        if (pref === undefined) {
          await prefsInterface.delete(currentUser.uuid, scopeUuid, key);
        } else {
          await prefsInterface.set(currentUser.uuid, scopeUuid, key, pref);
        }
        if (mounted.current) {
          setState({ value: pref, isLoading: false });
        }
      } catch (e) {
        if (e) {
          captureExceptionWithTags(e, {
            component: 'useScopedPref',
            preferenceKey: String(key),
            prefsScope: scopeType,
          });
        }
        if (mounted.current) {
          setState({ isLoading: false });
        }
      }
    },
    [currentUser?.uuid, scopeUuid, setState, prefsInterface, key, scopeType]
  );

  return [state.value, update, state.isLoading];
}

/** Hook to get and set a user preference scoped to a repository.
 * @param key The preference key.
 * @param repoUuid The repository UUID. If not provided, the current repository
 * from context will be used.
 *
 * Defaulting to the current repository is intended as a convenience, not just
 * a fallback; in most cases won't need to provide the UUID explicitly.
 */
export function useUserRepoPref(
  key: PreferenceKey,
  repoUuid: string | null = null,
  enabled: boolean = true
): UsePrefReturnType {
  const { repository } = useContext(CurrentDataContext);

  const resolvedUuid =
    ensureCurlyBrackets(repoUuid || repository?.uuid || '') || null;

  return useScopedPref(key, resolvedUuid, 'repository', enabled);
}

/** Hook to get and set a user preference scoped to a workspace.
 * @param key The preference key.
 * @param workspaceUuid The workspace UUID. If not provided, the current
 * workspace from context will be used.
 *
 * Defaulting to the current workspace is intended as a convenience, not just
 * a fallback; in most cases won't need to provide the UUID explicitly.
 */
export function useUserWorkspacePref(
  key: PreferenceKey,
  workspaceUuid: string | null = null,
  enabled: boolean = true
): UsePrefReturnType {
  const { workspace } = useContext(CurrentDataContext);

  const resolvedUuid =
    ensureCurlyBrackets(workspaceUuid || workspace?.uuid || '') || null;

  return useScopedPref(key, resolvedUuid, 'workspace', enabled);
}
